import React from "react";

import WPButton from "../WPButton";
import {
  MAX_VISIBLE_PAGES,
  PAGINTAION_DEFAULT_PAGE,
} from "./Pagination.constants";

import { combineClassNames } from "../../utils/appUtils";

/**
 * PaginationNumbers component renders a set of pagination buttons with ellipses when needed.
 * It displays up to MAX_VISIBLE_PAGES page numbers, showing the first and last page,
 * and uses ellipses to indicate skipped page ranges when there are too many pages.
 *
 * Props:
 * - currentPage: number - The currently active page.
 * - totalPages: number - Total number of pages.
 * - onPageChange: function - Callback when a page button is clicked.
 */
const PaginationNumbers = ({ currentPage, totalPages, onPageChange }) => {
  // Maximum number of visible page buttons (including first/last and ellipses)
  const paginationElements = [];

  const handlePaginationPageSelect = (event) => {
    const pageNumber = Number(event.target.name);
    if (pageNumber !== currentPage) {
      onPageChange(pageNumber);
    }
  };

  /**
   * Renders a single page button.
   * @param {number} pageNumber - The page number to render.
   */
  const renderPageButton = (pageNumber) => (
    <WPButton
      key={pageNumber}
      id={`page-btn-${pageNumber}`}
      name={pageNumber}
      onClick={handlePaginationPageSelect}
      size="small"
      variant="outlined"
      customClass={combineClassNames(
        "pagination-button",
        currentPage == pageNumber && "active"
      )}
    >
      {pageNumber}
    </WPButton>
  );

  /**
   * Renders an ellipsis ("...") to indicate skipped page numbers.
   * @param {string} key - Unique key for React rendering.
   */
  const renderEllipsis = (key) => <span key={key}>...</span>;

  /**
   * Pagination rendering logic:
   * - If total pages are within the visible limit, show all page buttons.
   * - If total pages exceed the visible limit:
   *   - Always show the first and last page buttons.
   *   - Show leading ellipsis if current page is beyond the 4th page.
   *   - Show trailing ellipsis if current page is before the last 3 pages.
   *   - Dynamically calculate a range of pages to display between ellipses.
   *   - Ensure the dynamic range fits within the max visible pages.
   *   - Add first page, ellipses (if needed), dynamic range, ellipses (if needed), and last page to pagination.
   */

  if (totalPages <= MAX_VISIBLE_PAGES) {
    for (let page = 1; page <= totalPages; page++) {
      paginationElements.push(renderPageButton(page));
    }
  } else {
    const LAST_PAGE = totalPages;

    const showLeadingEllipsis = currentPage > 4;
    const showTrailingEllipsis = currentPage < totalPages - 3;

    const alwaysVisiblePages = 2;
    const ellipsisCount =
      Number(showLeadingEllipsis) + Number(showTrailingEllipsis);
    const dynamicPageSlots =
      MAX_VISIBLE_PAGES - alwaysVisiblePages - ellipsisCount;

    let startPage = Math.max(2, currentPage - Math.floor(dynamicPageSlots / 2));
    let endPage = startPage + dynamicPageSlots - 1;

    if (endPage >= totalPages) {
      endPage = totalPages - 1;
      startPage = Math.max(2, endPage - dynamicPageSlots + 1);
    }

    paginationElements.push(renderPageButton(PAGINTAION_DEFAULT_PAGE));
    if (showLeadingEllipsis)
      paginationElements.push(renderEllipsis("leading-ellipsis"));

    for (let page = startPage; page <= endPage; page++) {
      paginationElements.push(renderPageButton(page));
    }

    if (showTrailingEllipsis)
      paginationElements.push(renderEllipsis("trailing-ellipsis"));
    paginationElements.push(renderPageButton(LAST_PAGE));
  }

  /** Return the array of pagination elements (buttons and ellipses) */
  return paginationElements;
};

export default PaginationNumbers;
